/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import {
    ContentBlock,
    TextBlock,
    Stack,
    FlexRow,
    FlexCol,
    SlideShowWithFetch,
    DividerNew as Divider,
    DisplayHtmlContent,
    Spacer,
    sluggify,
    Button,
    SecurityHelper,
} from 'sarsaparilla';
import { FacilityPageHeader } from 'shared-ui/components/FacilityPageHeader/FacilityPageHeader';
import { FacilityAlertBanner } from 'shared-ui/components/FacilityAlertBanner/FacilityAlertBanner';
import { NeedToKnowList } from 'shared-ui/components/NeedToKnowList/NeedToKnowList';
import {
    AnchorPageWithSidebarLayout,
    AnchorPageSection,
} from 'shared-ui/components/AnchorPageWithSidebarLayout';

import { InventoryReviewsSection } from 'ui-rating-review';

import { TourRatesContainer } from '../../containers/TourDetailsPage/TourRatesContainer';
import { BookingFormContainer } from '../../containers/TourDetailsPage/BookingFormContainer';
import { ToursContainer } from '../../containers/TourDetailsPage/ToursContainer';

import { BookingFormPlaceholder } from './BookingForm';

export function TourDetailsPage({
    facilityId,
    areTourDetailsLoading,
    facilityName,
    tourName,
    ratingSummary,
    tourId,
    introTextHtml,
    needToKnowItems,
    alerts,
}) {
    const [preventAutoScroll, setPreventAutoScroll] = useState(false);
    const handleRatingClick = () => {
        setPreventAutoScroll(false);
        const hash = sluggify(`Guest Reviews (${ratingSummary.reviewCount})`);
        window.location.href = `#${hash}`;
    };
    const handleScrollToAnchor = useCallback(() => {
        setPreventAutoScroll(true);
    }, []);
    const isWidget = SecurityHelper.isWidgetSession();

    return (
        <main className="te-tour-details-page">
            <FacilityAlertBanner alerts={alerts} />

            {!isWidget && (
                <div className="te-slideshow-mobile-container">
                    <SlideShowWithFetch
                        className="mb-2"
                        assetId={facilityId}
                        timedEntryTourId={`${facilityId}_${tourId}`}
                        slideInstructionsIdSuffix={'mobile'}
                    />
                </div>
            )}

            <ContentBlock>
                <Stack space="lg" className="mt-3">
                    <FlexRow>
                        <FlexCol lg={6} order={2} lgOrder={1}>
                            <Stack space="lg" className="te-tdp-top-stack">
                                <FacilityPageHeader
                                    isLoading={areTourDetailsLoading}
                                    heading={tourName}
                                    parentLinkText={facilityName}
                                    parentLinkUrl={`/timed-entry/${facilityId}`}
                                    isPartOfParent
                                    isInWidget={isWidget}
                                    rating={ratingSummary?.averageRating}
                                    reviewCount={ratingSummary?.reviewCount}
                                    mobileCoverageRating={
                                        ratingSummary?.averageMobileRating
                                    }
                                    onClickRatings={handleRatingClick}
                                    favoriteEntityId={tourId}
                                    favoriteEntityType={'timedentry_tour'}
                                    favoriteParentId={facilityId}
                                    favoriteParentType={'timedentry'}
                                />
                                {areTourDetailsLoading && <BookingFormPlaceholder />}
                                {!areTourDetailsLoading && <BookingFormContainer />}
                            </Stack>
                        </FlexCol>
                        <FlexCol lg={6} order={1} lgOrder={2}>
                            <div className="te-slideshow-desktop-container">
                                <Button
                                    className="p-1 mb-1 skip-to-overview-link"
                                    appearance="tertiary"
                                    size="sm"
                                    href="#skip-to-overview"
                                >
                                    Skip to Overview
                                </Button>
                                {!isWidget && (
                                    <SlideShowWithFetch
                                        assetId={facilityId}
                                        timedEntryTourId={`${facilityId}_${tourId}`}
                                        slideInstructionsIdSuffix={'desktop'}
                                    />
                                )}
                            </div>
                        </FlexCol>
                    </FlexRow>

                    {/* using to anchor skip link for overview section */}
                    <Divider id="skip-to-overview" />

                    {!areTourDetailsLoading && (
                        <AnchorPageWithSidebarLayout
                            preventScrollToAnchor={preventAutoScroll}
                            onScrollToAnchor={handleScrollToAnchor}
                        >
                            <Stack space="xl">
                                <AnchorPageSection heading="Overview">
                                    <TextBlock>
                                        <DisplayHtmlContent html={introTextHtml} />
                                    </TextBlock>
                                </AnchorPageSection>

                                <Divider />

                                <AnchorPageSection heading="Rates">
                                    <TourRatesContainer />
                                </AnchorPageSection>

                                <Divider />

                                <AnchorPageSection heading="Need To Know">
                                    <NeedToKnowList listItems={needToKnowItems} />
                                </AnchorPageSection>

                                {ratingSummary?.reviewCount > 0 && (
                                    <>
                                        <Divider />
                                        <AnchorPageSection
                                            heading={`Guest Reviews (${ratingSummary.reviewCount})`}
                                            headingLevel={4}
                                            gaTrackingId="086104295516"
                                        >
                                            <InventoryReviewsSection
                                                locationId={tourId}
                                                locationType="Tour"
                                                noHeading={true}
                                            />
                                        </AnchorPageSection>
                                    </>
                                )}
                            </Stack>
                        </AnchorPageWithSidebarLayout>
                    )}
                </Stack>
            </ContentBlock>
            <Spacer size="xl" />
            {!isWidget && (
                <ToursContainer
                    tourId={tourId}
                    areTourDetailsLoading={areTourDetailsLoading}
                />
            )}
        </main>
    );
}

TourDetailsPage.propTypes = {
    detailList: PropTypes.shape({
        distance: PropTypes.number,
        steps: PropTypes.number,
        effort: PropTypes.string,
        accessible: PropTypes.bool,
        duration: PropTypes.number,
    }),
    needToKnowItems: PropTypes.arrayOf(
        PropTypes.shape({
            subject: PropTypes.string,
            priority: PropTypes.string,
            description: PropTypes.node,
        })
    ),
    introTextHtml: PropTypes.string,
    facilityId: PropTypes.string,
    tourId: PropTypes.string,
    alerts: PropTypes.arrayOf(
        PropTypes.shape({
            body: PropTypes.string,
            id: PropTypes.string,
        })
    ),
    facilityName: PropTypes.string,
    tourName: PropTypes.string,
    ratingSummary: PropTypes.objectOf(PropTypes.number),
    areTourDetailsLoading: PropTypes.bool,
};

// cSpell:ignore timedentry
